/**
 * Vignette de recette sans photo (US-23).
 *
 * Le constat qui l'a motivée : l'espace réservé à l'image occupait 54 % de la
 * hauteur d'une carte pour ne rien montrer sur vingt recettes sur vingt. Un
 * rectangle vide répété vingt fois ne se lit pas comme « photo manquante », il
 * se lit comme « application inachevée » (audit externe, constat 12).
 *
 * La vignette ne cherche donc pas à imiter une photo : elle assume d'être un
 * signe. Dégradé déterministe tiré de l'identifiant — la même recette a
 * toujours la même vignette, d'un appareil à l'autre et d'un chargement à
 * l'autre, ce qui la rend reconnaissable — et initiale du titre en serif
 * (--font-display), la seule information qui distingue vraiment deux recettes
 * d'un coup d'œil.
 *
 * **Les six teintes ont été rabattues par la refonte éditoriale.** Elles
 * étaient prises dans « terracotta, olive, prune, sarcelle, ocre, ardoise »,
 * et deux d'entre elles — le sarcelle #2f6f6b et l'ocre #a06a1f — sont des
 * couleurs saturées, franches, qui n'appartiennent à aucune des deux couleurs
 * de la marque. Neuf recettes sans photo sur vingt-neuf suffisaient à poser
 * sur la bibliothèque des aplats vert-bleu et jaune vif au milieu des plats :
 * la grille cessait d'être une planche de photographies culinaires pour
 * devenir un nuancier, ce que la note d'intention de cette même vignette
 * refusait explicitement (« vingt vignettes côte à côte doivent rester une
 * bibliothèque, pas un nuancier »).
 *
 * Les remplaçantes restent six teintes distinctes, mais toutes désaturées
 * d'un cran et ramenées dans la famille terre : terre cuite, basilic sombre,
 * prune, ardoise chaude, ocre brûlée, brique. Elles se distinguent encore
 * l'une de l'autre — c'est leur unique fonction — sans se distinguer des
 * photos.
 *
 * Les six dégradés sont fixes et volontairement indépendants du thème : la
 * vignette est sa propre surface colorée, pas une surface de l'application.
 * Chaque teinte la plus claire est vérifiée à 4,5:1 au moins contre le blanc
 * de l'initiale (`server/test/client/contrast.test.ts`), donc conforme AA en
 * clair comme en sombre (§4 CLAUDE.md).
 *
 * Le « pictogramme de catégorie » prévu au brief attend US-21 : il n'existe
 * aujourd'hui aucune donnée de catégorie (les tags automatiques disent
 * « moins de 30 min » ou « sans four », pas « dessert »). Plutôt que de
 * deviner une catégorie à partir du titre — exactement le genre de résultat
 * faux mais crédible que le §3 de CLAUDE.md proscrit — la vignette porte
 * l'initiale et une marque d'assiette en filigrane.
 */

/**
 * Dégradés de fond, du plus clair au plus sombre de chaque paire. Teintes
 * issues de la palette de marque (terracotta, olive, prune, sarcelle, ocre,
 * ardoise) plutôt que d'un arc-en-ciel générique : vingt vignettes côte à côte
 * doivent rester une bibliothèque, pas un nuancier.
 */
export const THUMB_GRADIENTS: readonly (readonly [string, string])[] = [
  ['#9e4526', '#6b2d16'],
  ['#55673a', '#33401f'],
  ['#6d3a4e', '#42212e'],
  ['#41505e', '#28323c'],
  ['#8a6321', '#5a3e12'],
  ['#8e3f3a', '#592422'],
] as const;

/** Couleur de l'initiale, commune aux six dégradés. */
export const THUMB_FOREGROUND = '#ffffff';

/**
 * Hachage stable d'une chaîne. Volontairement trivial (base 31, modulo
 * premier) : il ne protège rien, il répartit seulement — et il doit donner le
 * même résultat aujourd'hui et dans deux ans, sinon la vignette d'une recette
 * changerait sous les yeux de son propriétaire.
 */
export function thumbHash(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) % 1000003;
  }
  return hash;
}

export function thumbGradient(id: string): readonly [string, string] {
  return THUMB_GRADIENTS[thumbHash(id) % THUMB_GRADIENTS.length] ?? ['#9e4526', '#6b2d16'];
}

/**
 * Initiale affichée : premier caractère **alphanumérique** du titre. Les
 * chiffres comptent (« 3 chocolats » donne « 3 »), la ponctuation d'ouverture
 * non (« « Le » vrai cassoulet » donne « L »). Repli sur « ? » plutôt que sur
 * une vignette muette.
 */
export function thumbInitial(title: string): string {
  const match = title.trim().match(/[\p{L}\p{N}]/u);
  return (match?.[0] ?? '?').toLocaleUpperCase('fr-FR');
}

export interface RecipeThumbProps {
  recipeId: string;
  title: string;
  className?: string;
}

/**
 * La densité s'adapte au point de rupture et non à une propriété : sous
 * 480 px la carte est horizontale et sa vignette fait 112 px de côté, où une
 * initiale de 48 px et un filigrane se marcheraient dessus. Une propriété
 * `compact` obligerait l'appelant à mesurer la fenêtre en JavaScript pour une
 * décision que le CSS prend seul, et correctement, au redimensionnement.
 */
export function RecipeThumb({ recipeId, title, className = '' }: RecipeThumbProps): React.JSX.Element {
  const [from, to] = thumbGradient(recipeId);
  return (
    <div
      data-testid={`recipe-thumb-${recipeId}`}
      data-thumb-from={from}
      // `aria-hidden` : le titre est juste à côté, en toutes lettres. Une
      // initiale annoncée à voix haute (« R, Ratatouille ») serait du bruit.
      aria-hidden="true"
      className={`relative flex h-full w-full items-center justify-center overflow-hidden ${className}`.trim()}
      style={{ backgroundImage: `linear-gradient(140deg, ${from} 0%, ${to} 100%)` }}
    >
      <svg
        viewBox="0 0 48 48"
        className="absolute -right-4 -bottom-4 hidden size-28 opacity-[0.12] xs:block"
        fill="none"
        stroke={THUMB_FOREGROUND}
        strokeWidth="1.5"
      >
        <circle cx="24" cy="24" r="13" />
        <circle cx="24" cy="24" r="5.5" />
      </svg>
      {/* `text-numeral` porte l'axe optique de Fraunces : une capitale de
          48 px dessinée avec les proportions d'un corps de texte paraît
          molle, et c'est le seul signe que porte la vignette. */}
      <span
        className="relative font-display text-4xl leading-none xs:text-6xl"
        style={{ color: THUMB_FOREGROUND, fontVariationSettings: "'opsz' 72, 'wght' 500" }}
      >
        {thumbInitial(title)}
      </span>
    </div>
  );
}
